import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { LayoutItem } from 'react-grid-layout';
import type { Chart as ApiChart, ChartLayoutItem } from '../api/types';
import { dashboardsService } from '../api';
import { chartsToLayout, autoArrangeLayout } from '../components/grid/layoutUtils';
import { handleLayoutHistoryShortcut } from './layoutHistoryShortcuts';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface LayoutSnapshot {
  items: LayoutItem[];
}

export interface UseLayoutEditorReturn {
  /** Current layout items for the grid */
  layout: LayoutItem[];
  /** Called on dragStop / resizeStop with the new layout */
  onLayoutChange: (newLayout: LayoutItem[]) => void;
  /** Undo last layout change */
  undo: () => void;
  /** Redo last undone change */
  redo: () => void;
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
  /** Reset to computed auto-arrange layout */
  autoArrange: () => void;
  /** Reset positions on server + auto-arrange locally */
  resetLayout: () => void;
  /** Whether a save is in-flight */
  isSaving: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_HISTORY = 50;
const AUTOSAVE_DEBOUNCE_MS = 500;

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useLayoutEditor(
  dashboardId: string,
  charts: ApiChart[],
): UseLayoutEditorReturn {
  // Derive initial layout from charts (uses saved positions when non-null)
  const [layout, setLayout] = useState<LayoutItem[]>(() => chartsToLayout(charts));

  const undoStack = useRef<LayoutSnapshot[]>([]);
  const redoStack = useRef<LayoutSnapshot[]>([]);
  const [, setHistoryVersion] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveVersionRef = useRef(0);
  const latestLayoutRef = useRef<LayoutItem[]>(layout);

  // Keep ref in sync
  latestLayoutRef.current = layout;

  // Re-derive layout when the set of charts changes (add/delete/reload).
  // We compare by chart IDs so that position-only updates (which produce a
  // new array reference) do NOT clear the undo/redo stacks.
  const chartIds = useMemo(() => charts.map((c) => c.id).join(','), [charts]);

  useEffect(() => {
    const newLayout = chartsToLayout(charts);
    setLayout(newLayout);
    // Clear history on chart list change
    undoStack.current = [];
    redoStack.current = [];
    setHistoryVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartIds]);

  // ── Autosave (debounced) ─────────────────────────────────────────────────

  const persistLayout = useCallback(
    (items: LayoutItem[]) => {
      if (!dashboardId) return;

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      saveTimerRef.current = setTimeout(async () => {
        const version = ++saveVersionRef.current;
        const layouts: ChartLayoutItem[] = items.map((item) => ({
          id: item.i,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        }));

        try {
          setIsSaving(true);
          await dashboardsService.updateChartsLayout(dashboardId, layouts);
        } catch (err) {
          console.error('Failed to save layout:', err);
        } finally {
          // Only clear saving state if this is still the latest save
          if (saveVersionRef.current === version) {
            setIsSaving(false);
          }
        }
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [dashboardId],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  // ── Push snapshot ────────────────────────────────────────────────────────

  const pushSnapshot = useCallback((items: LayoutItem[]) => {
    undoStack.current.push({ items: items.map((i) => ({ ...i })) });
    if (undoStack.current.length > MAX_HISTORY) {
      undoStack.current.shift();
    }
    redoStack.current = [];
    setHistoryVersion((v) => v + 1);
  }, []);

  // ── Layout change handler (called on dragStop / resizeStop) ──────────────

  const onLayoutChange = useCallback(
    (newLayout: LayoutItem[]) => {
      // Push current state to undo before applying new layout
      pushSnapshot(latestLayoutRef.current);
      setLayout(newLayout);
      persistLayout(newLayout);
    },
    [pushSnapshot, persistLayout],
  );

  // ── Undo ─────────────────────────────────────────────────────────────────

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;

    const snapshot = undoStack.current.pop()!;
    redoStack.current.push({
      items: latestLayoutRef.current.map((i) => ({ ...i })),
    });
    setHistoryVersion((v) => v + 1);

    setLayout(snapshot.items);
    persistLayout(snapshot.items);
  }, [persistLayout]);

  // ── Redo ─────────────────────────────────────────────────────────────────

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;

    const snapshot = redoStack.current.pop()!;
    undoStack.current.push({
      items: latestLayoutRef.current.map((i) => ({ ...i })),
    });
    setHistoryVersion((v) => v + 1);

    setLayout(snapshot.items);
    persistLayout(snapshot.items);
  }, [persistLayout]);

  // ── Auto-arrange (reset server positions to null + local recompute) ───────

  const autoArrange = useCallback(async () => {
    if (!dashboardId) return;

    pushSnapshot(latestLayoutRef.current);

    const arranged = autoArrangeLayout(charts);
    setLayout(arranged);

    // Reset all positionConfigs to null on the server so the DB stays
    // position-free.  The UI computes the layout on-the-fly from orderIndex.
    try {
      setIsSaving(true);
      await dashboardsService.resetChartsLayout(dashboardId);
    } catch (err) {
      console.error('Failed to reset layout:', err);
    } finally {
      setIsSaving(false);
    }
  }, [dashboardId, charts, pushSnapshot]);

  // ── Reset layout (server reset + local auto-arrange) ──────────────────────

  const resetLayout = useCallback(async () => {
    if (!dashboardId) return;

    pushSnapshot(latestLayoutRef.current);

    try {
      setIsSaving(true);
      await dashboardsService.resetChartsLayout(dashboardId);
    } catch (err) {
      console.error('Failed to reset layout:', err);
    } finally {
      setIsSaving(false);
    }

    const arranged = autoArrangeLayout(charts);
    setLayout(arranged);
  }, [dashboardId, charts, pushSnapshot]);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      handleLayoutHistoryShortcut(e, undo, redo);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  return {
    layout,
    onLayoutChange,
    undo,
    redo,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    autoArrange,
    resetLayout,
    isSaving,
  };
}